import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Trash2, Plus } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, Card, Field, TextInput, Btn, ConfirmDelete, Badge } from "@/components/admin/ui";
import { ImageField } from "@/components/admin/Media";

const BLANK = { title: "", image: "", category: "Showroom", order: 0 };

export default function GalleryAdmin() {
  const qc = useQueryClient();
  const [form, setForm] = useState(BLANK);
  const { data: items = [], isLoading } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data, staleTime: 0 });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ["gallery"] }); qc.invalidateQueries({ queryKey: ["admin-stats"] }); };

  const create = useMutation({
    mutationFn: async (body) => api.post("/admin/gallery", { ...body, order: Number(body.order) || 0 }),
    onSuccess: () => { toast.success("Image added"); setForm(BLANK); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/gallery/${id}`),
    onSuccess: () => { toast.success("Image removed"); invalidate(); },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div data-testid="admin-gallery-page">
      <PageTitle eyebrow="Content" title="Gallery" />
      <div className="grid gap-6 xl:grid-cols-3">
        <Card title="Add image" className="xl:order-2">
          <form onSubmit={(e) => { e.preventDefault(); if (!form.title || !form.image) return toast.error("Title and image URL are required."); create.mutate(form); }} className="space-y-4" data-testid="gallery-add-form">
            <Field label="Title"><TextInput value={form.title} onChange={set("title")} placeholder="Showroom floor" data-testid="gallery-title-input" /></Field>
            <Field label="Image"><ImageField value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} placeholder="Upload or paste URL" testid="gallery-image" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Category"><TextInput value={form.category} onChange={set("category")} placeholder="Showroom" data-testid="gallery-category-input" /></Field>
              <Field label="Order"><TextInput type="number" value={form.order} onChange={set("order")} data-testid="gallery-order-input" /></Field>
            </div>
            {form.image && <img src={form.image} alt="" className="h-32 w-full rounded-md border border-white/10 object-cover" />}
            <Btn type="submit" disabled={create.isPending} className="w-full" data-testid="gallery-add-button">{create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add to gallery</Btn>
          </form>
        </Card>

        <div className="xl:col-span-2 xl:order-1">
          {isLoading ? <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div> : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-gallery-grid">
              {items.map((g) => (
                <div key={g.id} className="admin-card group relative overflow-hidden" data-testid={`admin-gallery-item-${g.id}`}>
                  <img src={g.image} alt={g.title} className="h-44 w-full object-cover" />
                  <div className="flex items-start justify-between gap-3 p-4">
                    <div className="min-w-0"><p className="truncate font-display font-semibold">{g.title}</p><div className="mt-1 flex gap-2"><Badge>{g.category}</Badge><span className="font-mono text-[10px] text-slate-500">#{g.order}</span></div></div>
                    <ConfirmDelete title="Remove this image?" onConfirm={() => remove.mutate(g.id)} testid={`gallery-delete-${g.id}`}>
                      <button data-testid={`gallery-delete-${g.id}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-white/10 text-slate-300 hover:border-crimson hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
                    </ConfirmDelete>
                  </div>
                </div>
              ))}
              {!items.length && <p className="col-span-full py-12 text-center text-slate-500">No images yet.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
